import Cookies, { type CookieSetOptions } from "universal-cookie";

/**
 * Shared cookie jar for the marketplace app.
 *
 * js-cookie defaulted every write to `path: "/"`; universal-cookie has no default
 * path (it would scope the cookie to the current page), so pin it on the jar.
 */
export const cookieJar = new Cookies(null, { path: "/" });

/**
 * Read a cookie as the raw string js-cookie used to return. universal-cookie
 * JSON-parses values by default, which would turn stored JSON into objects and
 * numeric-looking values into numbers.
 */
export const RAW_COOKIE = { doNotParse: true } as const;

/**
 * js-cookie accepted `expires` as a number of days; universal-cookie needs an
 * explicit `Date`, resolved at write time.
 */
function expiresInDays(days: number): CookieSetOptions {
  return { expires: new Date(Date.now() + days * 24 * 60 * 60 * 1000) };
}

export type CookieConfig = {
  name: string;
  /**
   * Cookie lifetime in DAYS. Entries marked `LEGACY BUG` below hold second counts
   * that js-cookie has always read as days, so they are effectively permanent
   * cookies (~year 9000) rather than the 30/365 days they look like. Those values
   * are kept verbatim here so this migration changes no cookie's lifetime;
   * correcting them is a separate, behavior-changing follow-up.
   */
  expiresDays: number;
};

export function cookieSetOptions(cookie: CookieConfig): CookieSetOptions {
  return expiresInDays(cookie.expiresDays);
}

const cookies = {
  sourceParentRedirectEmail: {
    name: "dojo-tutoring-parent-source-redirect-email",
    expiresDays: 60 * 60 * 24 * 30, // LEGACY BUG: 2,592,000 days, not 30. Preserved.
  },
  sourceTutorRedirectEmail: {
    name: "dojo-tutoring-tutor-source-redirect-email",
    expiresDays: 60 * 60 * 24 * 30, // LEGACY BUG: 2,592,000 days, not 30. Preserved.
  },
  sourceUtmCampaigns: {
    name: "dojo-tutoring-utm-campaigns",
    expiresDays: 60 * 60 * 24 * 365, // LEGACY BUG: 31,536,000 days, not 365. Preserved.
  },
  parentDataForStepOneSkipping: {
    name: "dojo-tutoring-parent-data-for-step-one-skipping",
    expiresDays: 60 * 60 * 24 * 30, // LEGACY BUG: 2,592,000 days, not 30. Preserved.
  },
  promoCode: {
    name: "dojo-tutoring-promo-code",
    expiresDays: 60 * 60 * 24 * 30, // LEGACY BUG: 2,592,000 days, not 30. Preserved.
  },
  metaFbp: {
    name: "_fbp",
    expiresDays: 90, // Meta attribution cookies should live for 90 days.
  },
  metaFbc: {
    name: "_fbc",
    expiresDays: 90, // Meta attribution cookies should live for 90 days.
  },
  // OpenAI (ChatGPT ads) click reference. First-party JS cookie (same pattern as _fbc/_fbp)
  // so it survives in-app browsers that drop the redirect-set dojo_clickId cookie; read at
  // checkout as the oppref payload fallback. Named dojo_oppref to avoid colliding with the
  // OpenAI pixel's own __oppref cookie should the pixel ever be installed.
  openaiOppref: {
    name: "dojo_oppref",
    expiresDays: 30, // Matches the campaign's 30-day click-through attribution window.
  },
} as const satisfies Record<string, CookieConfig>;

export default cookies;
